'use client';

import { m } from 'motion/react';
import { useLanguage } from '@/context/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import Image from 'next/image';

export default function About() {
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    return (
        <section id="about" className="py-24 bg-black relative border-t border-white/5 overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-[#006546]/8 blur-[100px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">

                    <m.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
                            {t('about.title.1')} <span className="text-[#19ffb5]">{t('about.title.2')}</span>
                        </h2>

                        <p className="text-lg text-gray-400 mb-6 leading-relaxed font-light">
                            {t('about.description.1')}
                        </p>
                        <p className="text-lg text-gray-400 leading-relaxed mb-12 font-light">
                            {t('about.description.2')}
                        </p>

                        <div className="grid grid-cols-2 gap-8 pt-8 border-t border-white/10">
                            <div>
                                <p className="text-5xl font-semibold text-white mb-2">{t('about.stat.1.value')}</p>
                                <p className="text-xs font-semibold text-[#19ffb5] uppercase tracking-wider">{t('about.stat.1.label')}</p>
                            </div>
                            <div>
                                <p className="text-5xl font-semibold text-white mb-2">{t('about.stat.2.value')}</p>
                                <p className="text-xs font-semibold text-[#19ffb5] uppercase tracking-wider">{t('about.stat.2.label')}</p>
                            </div>
                        </div>
                    </m.div>

                    <m.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className="relative"
                    >
                        <div className="relative h-[500px] w-full mt-10 lg:mt-0">
                            {/* Process Image 1 */}
                            <div className="absolute right-0 top-0 w-3/4 h-[380px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl z-10">
                                <div className="absolute inset-0 bg-[#006546]/15 mix-blend-overlay z-10" />
                                <Image
                                    src="/quem-somos.webp"
                                    alt="Webzuk Office"
                                    fill
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    quality={80}
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </div>

                            {/* Process Image 2 (Overlapping) */}
                            <div className="absolute left-0 bottom-0 w-2/3 h-[320px] rounded-3xl overflow-hidden border-4 border-black shadow-[0_20px_50px_rgba(0,0,0,0.8)] z-20">
                                <Image
                                    src="/quem-somos.webp"
                                    alt="Webzuk Team"
                                    fill
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    quality={80}
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        {/* Floating Badge */}
                        <div className="absolute -bottom-6 right-6 bg-white border border-white/10 px-6 py-4 rounded-2xl shadow-2xl z-30">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#006546]/15 rounded-full flex items-center justify-center">
                                    <div className="w-3 h-3 bg-[#19ffb5] rounded-full animate-ping" />
                                </div>
                                <p className="text-black font-semibold leading-none text-lg">Tech<br /><span className="text-gray-500 font-light text-sm">Driven</span></p>
                            </div>
                        </div>
                    </m.div>

                </div>
            </div>
        </section>
    );
}
